import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useMainPerformanceCustomerRetention, SectionProps } from '../../hooks/useMainPerformanceSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors } from '../../utils/themeHelpers';
import ChartControls from '../ui/ChartControls';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface CustomerRetentionAnalysisProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const CustomerRetentionAnalysis: React.FC<CustomerRetentionAnalysisProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: customerRetentionData, isLoading, isError, error, refetch } = useMainPerformanceCustomerRetention(branchId, timeRange);

  // Component metadata
  const componentId = 'customer-retention-analysis';
  const componentTitle = 'تحلیل حفظ مشتریان';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Helper function to format numbers for display
  const formatKpiValue = (value: number | string, type: 'currency' | 'percentage' | 'number' = 'number'): string => {
    if (typeof value === 'string') return value;
    if (!value && value !== 0) return '0';

    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
      case 'percentage':
        return `${value != null ? value.toFixed(1) : '0'}%`;
      default:
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
    }
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-64">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری تحلیل حفظ مشتریان…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="خطا در دریافت اطلاعات تحلیل حفظ مشتریان"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!customerRetentionData || !Array.isArray(customerRetentionData) || customerRetentionData.length === 0) {
    return (
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="mainPerformance"
          onInfo={() => openGuide(CHART_GUIDES.customerRetention)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'mainPerformance',
            componentType: 'chart',
            description: 'تحلیل نرخ بازگشت و وفاداری مشتریان',
            props: { branchId, timeRange, compact },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6',
              companions: []
            }
          })}
          isFavorited={isFavorited(componentId)}
          className="mb-4"
          chartRef={chartRef}
        />
        <div className="mb-6">
          <h3 className="title-card">{componentTitle}</h3>
          <p className="text-text-secondary text-sm mt-1 text-center">
            مقایسه نرخ جذب و ریزش مشتریان          </p>
        </div>
        <div style={{ height: 400, width: '100%' }}>
          <EmptyState icon="🔄" className="animate-enter" />
        </div>
      </div>
    );
  }

  const newCustomersData = customerRetentionData.map(item => item.newCustomers || 0);
  const returningCustomersData = customerRetentionData.map(item => item.returningCustomers || 0);
  const lostCustomersData = customerRetentionData.map(item => item.lostCustomers || 0);

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.customerRetention)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'تحلیل نرخ بازگشت و وفاداری مشتریان',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 gap-6',
            companions: []
          }
        })}
        isFavorited={isFavorited(componentId)}
        className="mb-4"
        chartRef={chartRef}
      />
      <div className="mb-6">
        <h3 className="title-card">{componentTitle}</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">
         مقایسه نرخ جذب و ریزش مشتریان 
        </p>
      </div>
      <div style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار تحلیل حفظ مشتریان">
        <Line
          data={{
            labels: customerRetentionData.map(item => item.period || item.month || ''),
            datasets: [
              {
                label: 'مشتریان جدید',
                data: newCustomersData,
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartColors(currentTheme)[0],
                borderWidth: 3,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                fill: false
              },
              {
                label: 'مشتریان برگشتی',
                data: returningCustomersData,
                borderColor: getChartColors(currentTheme)[1],
                backgroundColor: getChartColors(currentTheme)[1],
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: getChartColors(currentTheme)[1],
                tension: 0.4,
                fill: false
              },
              {
                label: 'مشتریان غیر فعال',
                data: lostCustomersData,
                borderColor: getChartColors(currentTheme)[2],
                backgroundColor: getChartColors(currentTheme)[2],
                borderWidth: 3,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: getChartColors(currentTheme)[2],
                tension: 0.4,
                fill: false
              }
            ]
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                display: true,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function (context: any) {
                    const datasetLabel = context.dataset.label || '';
                    const value = context.parsed.y;
                    const separator = '\u00A0:\u00A0';
                    const formattedValue = `${formatKpiValue(value)} نفر`;
                    return `${datasetLabel}${separator}${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                display: true,
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function (value: any) {
                    return formatKpiValue(value);
                  },
                },
                title: {
                  display: true,
                  text: 'تعداد مشتریان',
                  color: 'var(--chart-axis-color)',
                  font: {
                    size: 16,
                    weight: 700,
                    family: 'var(--font-primary)',
                  },
                  padding: 15,
                },
              },
            },
          } as any}
        />
      </div>

      {/* Data Summary for normalized chart */}
      <div className="mt-6 p-4 bg-bg-secondary rounded-lg">
        <h4 className="text-sm font-medium text-text-primary mb-3">خلاصه داده‌ها (آخرین ماه)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-text-tertiary mb-1">مشتریان جدید</div>
            <div className="text-lg font-semibold text-text-primary">
              {formatKpiValue(customerRetentionData[customerRetentionData.length - 1]?.newCustomers || 0)} نفر
            </div>
          </div>
          <div className="text-center">
            <div className="text-text-tertiary mb-1">مشتریان برگشتی</div>
            <div className="text-lg font-semibold text-text-primary">
              {formatKpiValue(customerRetentionData[customerRetentionData.length - 1]?.returningCustomers || 0)} نفر
            </div>
          </div>
          <div className="text-center">
            <div className="text-text-tertiary mb-1">مشتریان غیر فعال</div>
            <div className="text-lg font-semibold text-status-error">
              {formatKpiValue(customerRetentionData[customerRetentionData.length - 1]?.lostCustomers || 0)} نفر
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRetentionAnalysis;